/**
 * EmailPreviewPanel - Phase 10a.1 Email Trust & Test Foundation
 *
 * Renders a preview of the email HTML in a sandboxed iframe.
 * Shows loading skeleton, error state, and "What your client will see" header.
 * Uses srcdoc + sandbox="allow-same-origin" for security (no scripts, forms, or navigation).
 */
import { useRef, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface EmailPreviewPanelProps {
  html: string | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
}

export function EmailPreviewPanel({
  html,
  isLoading,
  error,
  onRetry,
}: EmailPreviewPanelProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Resize iframe to match its content height
  const syncIframeHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.body) {
        const contentHeight = doc.body.scrollHeight;
        iframe.style.height = `${Math.max(contentHeight + 20, 200)}px`;
      }
    } catch {
      iframe.style.height = '70vh';
    }
  }, []);

  // Auto-resize iframe to content height after load + observe ongoing changes
  const handleIframeLoad = useCallback(() => {
    syncIframeHeight();

    // Attach ResizeObserver to dynamically track content size changes
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc?.body) {
        const observer = new ResizeObserver(() => {
          syncIframeHeight();
        });
        observer.observe(doc.body);
        observerRef.current = observer;
      }
    } catch {
      // Content access failed — initial height already set, no observer needed
    }
  }, [syncIframeHeight]);

  const observerRef = useRef<ResizeObserver | null>(null);

  // Clean up observer and re-trigger on html changes
  useEffect(() => {
    // Disconnect previous observer when html changes
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (html && iframeRef.current) {
      const timer = setTimeout(syncIframeHeight, 100);
      return () => clearTimeout(timer);
    }
  }, [html, syncIframeHeight]);

  // Clean up observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="email-preview-panel"
        className="rounded-lg border bg-muted/30 p-6"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-4">
          <Eye className="w-4 h-4" />
          What your client will see
        </div>
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2
            className="w-6 h-6 animate-spin text-muted-foreground"
            data-testid="email-preview-loading"
          />
          <p className="text-sm text-muted-foreground">Loading preview...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        data-testid="email-preview-panel"
        className="rounded-lg border border-destructive/50 bg-destructive/5 p-6"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-4">
          <Eye className="w-4 h-4" />
          What your client will see
        </div>
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <AlertCircle className="w-6 h-6 text-destructive" />
          <p className="text-sm text-destructive">
            Failed to load email preview
          </p>
          <p className="text-xs text-muted-foreground">{error.message}</p>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              data-testid="email-preview-retry"
            >
              Retry
            </Button>
          )}
        </div>
      </div>
    );
  }

  // No HTML yet (shouldn't normally happen, but handle gracefully)
  if (!html) {
    return null;
  }

  // Success state - render HTML in sandboxed iframe
  return (
    <div
      data-testid="email-preview-panel"
      className="rounded-lg border bg-muted/30 p-4 animate-in fade-in-0 duration-200"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
        <Eye className="w-4 h-4" />
        What your client will see
      </div>
      <div className="rounded border bg-white overflow-y-auto">
        <iframe
          ref={iframeRef}
          data-testid="email-preview-iframe"
          srcDoc={html}
          sandbox="allow-same-origin"
          title="Email preview"
          className="w-full border-0"
          style={{ minHeight: '200px', overflow: 'hidden' }}
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
}
